import {Nullable} from "../../models/common/Nullable";
import {APP_CONFIG} from "../../config/GeneralConfig";
import {ErrorAction, ReduxAction} from "../../models/common/ActionTypes";
import {Observable, of} from "rxjs";
import {ofType, StateObservable} from "redux-observable";
import {AppState} from "../../reducers/RootReducer";
import {catchError, mergeMap, switchMap} from "rxjs/operators";
import {ajax} from "rxjs/ajax";
import { RouteMethods} from "../../config/RouteConfig";
import {eventNotificationError, eventNotificationSuccess} from "../common/EventNotificationActions";
import {ProjectStagePhaseTaskEmployee} from "../../models/projects/ProjectStagePhaseTaskEmployee";
import {Identifier} from "../../models/common/Identifier";
import {fetchProjectById} from "./ProjectActions";
import {fetchProjectEmployees} from "./ProjectStagePhaseEmployeeActions";
import {getInitialViewSettings} from "../../models/common/ViewSettings";


/**
 * Constructs a URI path for accessing project and employee data.
 *
 * This function generates a URL path that aligns with the specific API structure,
 * either for listing all projects or accessing employees within a specific project.
 * It ensures consistency with the base web configuration of the application.
 *
 * @param id - The unique identifier of the project. If `null`, the method
 *             returns the path for all projects. Otherwise, this ID is used
 *             to construct a path for fetching employees within the specified project.
 * @returns The constructed API path as a string.
 * @throws Will not throw errors but relies on `APP_CONFIG.baseWeb` being correctly configured.
 */
function apiPath( id: Nullable< number > ): string {
    if( id === null ) {
        return APP_CONFIG.baseWeb + 'projects/';
    } else {
        return `${ APP_CONFIG.baseWeb }projects/${ id }/employees`;
    }
}

/** union of all Project related action types */
export type ProjectEmployeeViewActionTypes = PersistProjectEmployeeActionTypes


/**
 * CREATE PROJECT redux action interface
 */
export interface PersistProjectEmployeeAction extends ReduxAction<typeof PERSIST_PROJECT_EMPLOYEES> {
    projectEmployees: ProjectStagePhaseTaskEmployee[]
    projectId: Identifier<number>
}

/**
 * Represents a Redux action dispatched when project employees are successfully persisted.
 *
 * This action serves to notify the application state management layer of a successful operation
 * involving the saving or updating of project-specific employee data. It is commonly used
 * in the context of ensuring application consistency and informing downstream components
 * of the success state.
 *
 * @extends ReduxAction<typeof PERSIST_PROJECT_EMPLOYEES_SUCCESS>
 * Extends the base Redux action to include a pre-defined action type specific to
 * employee persistence success events.
 */
export interface PersistProjectEmployeeSuccessAction extends ReduxAction<typeof PERSIST_PROJECT_EMPLOYEES_SUCCESS> {
}

/**
 * Represents an error action that occurs when persisting project employees fails.
 *
 * This interface is used to handle and track errors related to saving or updating employee data
 * within the context of a specific project. It provides a standardized error action structure
 * that integrates with the application's state management system.
 *
 * @extends ErrorAction<typeof PERSIST_PROJECT_EMPLOYEES_ERROR>
 * Extends the base `ErrorAction` with a specific error type relevant to persisting project employees.
 */
export interface PersistProjectEmployeeErrorAction extends ErrorAction<typeof PERSIST_PROJECT_EMPLOYEES_ERROR> {
}

/**
 * Represents all possible action types related to persisting project employee information in the system.
 *
 * This type is a union of multiple specific actions that capture the different stages or outcomes
 * of the persistence process. It enables type safety and clarity when managing the relevant actions
 * within a state management system, such as Redux.
 *
 * This type is essential for ensuring the correct handling of employee-related data updates, including
 * initiating the persistence process, handling success responses, and managing errors.
 */
export type PersistProjectEmployeeActionTypes = PersistProjectEmployeeAction | PersistProjectEmployeeSuccessAction | PersistProjectEmployeeErrorAction

/**
 * Represents the action type for persisting the association between projects and their assigned employees.
 *
 * This constant is used to trigger or identify actions related to saving or updating the mapping of employees assigned to specific projects within the system. It ensures that any changes to project-employee relationships are retained across sessions or updates, facilitating consistent data management.
 *
 * Developers should use this action type within the appropriate context of state management or API calls related to project-employee persistence.
 */
export const PERSIST_PROJECT_EMPLOYEES = "PERSIST_PROJECT_EMPLOYEES";
/**
 * Represents the success state after employees have been persisted to a project.
 *
 * This constant is used as an action type to indicate that the operation of saving
 * or updating project employees in the system has been successfully completed.
 * It aids in identifying the success event in state management or throughout the
 * application's workflow.
 */
export const PERSIST_PROJECT_EMPLOYEES_SUCCESS = "PERSIST_PROJECT_EMPLOYEES_SUCCESS";
/**
 * Represents an error state indicating a failure to persist project employee data.
 *
 * This error is used to communicate that saving employee information associated with a project
 * to a persistent storage system has encountered an issue. It helps in diagnosing and handling
 * failures related to project employee data management.
 */
export const PERSIST_PROJECT_EMPLOYEES_ERROR = "PERSIST_PROJECT_EMPLOYEES_ERROR";

/**
 * Saves the association between employees and a specific project.
 *
 * This function is used to persist the mapping of employees to a project,
 * ensuring that their roles and contributions within the project are properly recorded.
 * It generates an action for data handling or state management purposes.
 *
 * @param projectEmployees - An array of objects representing the employees
 * who are assigned to various stages, phases, or tasks of the project. Each object
 * must adhere to the `ProjectStagePhaseTaskEmployee` structure.
 * @param projectId - The unique identifier of the project to which the employees are being assigned.
 * Must conform to the `Identifier<number>` type format.
 * @return An action object of type `PersistProjectEmployeeAction`, which includes the
 * project ID and the list of associated project employees. This object can be dispatched
 * to a store or used for further processing.
 * @throws Will throw an error if any of the provided parameters are invalid or missing.
 */
export function persistProjectEmployee(
    projectEmployees: ProjectStagePhaseTaskEmployee[] ,
    projectId: Identifier<number>
): PersistProjectEmployeeAction {
    return {  type: PERSIST_PROJECT_EMPLOYEES, projectEmployees, projectId };
}

/**
 * Dispatches an action indicating the successful persistence of project-employee associations.
 *
 * This function generates an action object used in state management to signal that the process
 * of saving the relationship between a project and its associated employees has been completed successfully.
 * It helps in updating the application state and triggering any related updates in the UI or other components.
 *
 * @return Returns an action object with the `type` property set to `PERSIST_PROJECT_EMPLOYEES_SUCCESS`,
 * which can be intercepted by reducers or middleware to handle the success state of the persistence operation.
 */
export function persistProjectEmployeeSuccess( ): PersistProjectEmployeeSuccessAction {
    return { type: PERSIST_PROJECT_EMPLOYEES_SUCCESS };
}

/**
 * Creates an action to record an error encountered while persisting project employee data.
 *
 * This function is used to generate an action indicating a failure occurred during
 * the persist operation for project employee information. It helps in tracking errors
 * and enabling error handling workflows in the application.
 *
 * @param error - The error object or message that represents the issue encountered
 *                during the persist operation. This can include details like error codes
 *                or stack traces for debugging.
 * @return An action object containing the type `PERSIST_PROJECT_EMPLOYEES_ERROR`
 *         and the provided error details. This object is typically dispatched to the
 *         application's state management system (e.g., Redux) to update the state.
 */
export function persistProjectEmployeeError( error: any ): PersistProjectEmployeeErrorAction {
    return { type: PERSIST_PROJECT_EMPLOYEES_ERROR,  error };
}

/**
 * Persists a list of employees assigned to a specific project and triggers related actions upon success or failure.
 *
 * This function listens for a specific Redux action to persist project employee data to the server
 * and handles success and error scenarios by dispatching the appropriate follow-up actions.
 * It facilitates the synchronization of project employee data between the client and server.
 *
 * @param action$ - An Observable stream of dispatched Redux actions.
 *                  It listens for actions of type `PERSIST_PROJECT_EMPLOYEES` to trigger the epic.
 * @param store$ - A StateObservable that provides access to the current Redux store state,
 *                 enabling actions to use shared application state if needed.
 *
 * @return An Observable that emits follow-up actions based on the result of the persistence request.
 *         On success, it emits actions to refresh project employee data, notify success, and fetch the project details.
 *         On error, it emits actions to handle persistence failure and log an error notification.
 *
 * @throws May emit additional error-handling actions if the server request fails.
 */
export function persistProjectEmployeeEpic(
    action$: Observable< PersistProjectEmployeeAction >,
    store$: StateObservable< AppState >
) {
    return action$.pipe(
        ofType( PERSIST_PROJECT_EMPLOYEES ),
        switchMap( action => {
            
            return ajax( APP_CONFIG.getRequestObject(
                apiPath( action.projectId.rawValue ),
                RouteMethods.post,
                action.projectEmployees
            ) ).pipe(
                mergeMap( _ => of(
                    persistProjectEmployeeSuccess(),
                    fetchProjectEmployees( action.projectId, getInitialViewSettings("project_employee", 0) ),
                    eventNotificationSuccess(),
                    fetchProjectById( action.projectId )
                ) ),
                catchError( error => {

                    return of(
                        persistProjectEmployeeError(error),
                        eventNotificationError(
                            error,
                            store$,
                            'projects_employee',
                            'persistProjectEmployee'
                        )
                    )
                } )
            );
        } )
    )
}